import { useState } from 'react';
import { Box, Avatar, Typography, Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PositionOrEmpty, Reward, PositionModalData, Position } from './types';
import { RewardsModal } from './RewardsModal';

// ─── Reward badge config ────────────────────────────────────────────────────

interface BadgeDef {
    key: keyof Reward;
    color: string;
    label: string;
}

const BADGE_DEFS: BadgeDef[] = [
    { key: 'main_award', color: '#F59E0B', label: 'Основное' },
    { key: 'referal_1', color: '#3B82F6', label: 'Реф. 1' },
    { key: 'referal_2', color: '#60A5FA', label: 'Реф. 2' },
    { key: 'referal_3', color: '#93C5FD', label: 'Реф. 3' },
    { key: 'clone_balance', color: '#A855F7', label: 'Баланс кл.' },
    { key: 'upgrade', color: '#44A437', label: 'Апгрейд' },
    { key: 'clone_reward', color: '#FF6B2B', label: 'Клон' },
];

const hasSpecial = (reward: Reward) => !!(reward.special && reward.special.length > 0);

// ─── Component ──────────────────────────────────────────────────────────────

interface Props {
    position: PositionOrEmpty;
    isMentor: boolean;
    reward?: Reward;
    slotIndex?: number;
    currency: string;
}

export const PositionCell = ({ position, isMentor, reward, slotIndex, currency }: Props) => {
    const [open, setOpen] = useState(false);

    const pos = position && typeof position !== 'boolean' ? position : null;
    const avatarSrc = pos?.avatar ? (pos.avatar as string) : undefined;
    const login = pos?.login || (isMentor ? '?' : 'Пусто');
    const isEmpty = !pos;

    const activeBadges = reward ? BADGE_DEFS.filter((b) => !!reward[b.key]) : [];
    const showSpecial = reward ? hasSpecial(reward) : false;

    const isClickable = isMentor ? !!pos : !!reward;

    const getModalData = (): PositionModalData | null => {
        if (isMentor && pos) return { kind: 'mentor', position: pos as Position };
        if (!isMentor && reward) return { kind: 'slot', reward, position, slotIndex: slotIndex ?? 1 };
        return null;
    };

    return (
        <>
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    pt: 2.5,   // space for the info button at top
                    pb: 0.75,
                    borderRadius: 2,
                    cursor: isClickable ? 'pointer' : 'default',
                    transition: 'background 0.15s',
                    '&:hover': isClickable ? { bgcolor: 'action.hover' } : {},
                }}
                onClick={() => isClickable && setOpen(true)}
            >
                {/* ── Info button — always visible ── */}
                {isClickable && (
                    <Tooltip title="Нажмите для подробной информации" placement="top" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                            sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                p: 0.25,
                                color: 'primary.main',
                                bgcolor: 'background.paper',
                                boxShadow: 1,
                                '&:hover': { bgcolor: 'background.paper', opacity: 0.85 },
                            }}
                        >
                            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                )}

                {/* ── Avatar ── */}
                <Avatar
                    src={avatarSrc}
                    sx={{
                        width: isMentor ? 60 : 48,
                        height: isMentor ? 60 : 48,
                        bgcolor: isEmpty ? 'grey.700' : isMentor ? 'primary.main' : 'secondary.main',
                        border: isMentor ? '2px solid' : undefined,
                        borderColor: isMentor ? 'primary.main' : undefined,
                        fontSize: isMentor ? '1.5rem' : '1.1rem',
                    }}
                >
                    {!avatarSrc && (isEmpty ? '?' : login[0]?.toUpperCase())}
                </Avatar>

                {/* ── Login ── */}
                <Typography
                    variant="caption"
                    sx={{
                        color: isEmpty ? 'text.main' : 'text.main',
                        textAlign: 'center',
                        fontSize: isMentor ? '0.9rem' : '0.9rem',
                        fontWeight: isMentor ? 600 : 500,
                        maxWidth: 90,
                        wordBreak: 'break-word',
                        lineHeight: 1.3,
                    }}
                >
                    {login}
                </Typography>

                {/* ── Куратор под ментором ── */}
                {isMentor && pos?.kurator && (
                    <Typography variant="caption" color="text.main" sx={{ fontSize: '0.85rem' }}>
                        ({pos.kurator})
                    </Typography>
                )}

                {/* ── Reward badges (child slots only) ── */}
                {!isMentor && (activeBadges.length > 0 || showSpecial) && (
                    <Box display="flex" flexWrap="wrap" justifyContent="center" gap={0.4} maxWidth={90} mt={0.25}>
                        {activeBadges.map((b) => (
                            <Tooltip key={b.key} title={b.label} placement="top" arrow>
                                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: b.color, flexShrink: 0 }} />
                            </Tooltip>
                        ))}
                        {showSpecial && (
                            <Tooltip title="Спец. награды" placement="top" arrow>
                                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#FFD700', flexShrink: 0 }} />
                            </Tooltip>
                        )}
                    </Box>
                )}
            </Box>

            <RewardsModal
                open={open}
                onClose={() => setOpen(false)}
                data={getModalData()}
                currency={currency}
            />
        </>
    );
};